import { apiConfig } from '../config/api';
import { apiService } from './api';
import { userActionLogger } from './userActionLogger';
import { extractAuthErrorMessage, extractRequiresCaptcha, extractCaptchaError, extractLockoutMessageFields } from './loginCaptchaService';
import type { TwoFactorStatusDto } from './twoFactorService';
import { clearLearnerPersistedFilters } from '../utils/learnerFilterPersistence';

export interface LoginDto {
  email: string;
  password: string;
  rememberMe?: boolean;
  captchaId?: string;
  captchaCode?: string;
}

export interface RegisterDto {
  email: string;
  password: string;
  confirmPassword?: string;
}

export interface EmailVerificationDto {
  email: string;
  verificationCode: string;
}

export interface PhoneVerificationDto {
  phoneNumber: string;
  verificationCode: string;
}

export interface PhoneAvailabilityDto {
  isAvailable: boolean;
  isValid: boolean;
  phoneNumber: string;
  message: string;
  messageKey?: string;
  errorCode?: string;
}

export interface PhoneVerificationSendResultDto {
  success: boolean;
  message: string;
  messageKey?: string;
  errorCode?: string;
  telegramLinked: boolean;
  cooldownSeconds: number;
  expiresAt?: string | null;
  developmentCode?: string | null;
}

export interface PhoneVerificationConfirmResultDto {
  success: boolean;
  message: string;
  messageKey?: string;
  errorCode?: string;
  profile?: UserProfileDto;
}

export interface SendVerificationCodeDto {
  email?: string;
  phoneNumber?: string;
}

export interface UserProfileDto {
  id: string;
  email: string;
  userName?: string;
  phoneNumber?: string;
  phoneNumberConfirmed?: boolean;
  firstName?: string;
  lastName?: string;
  bio?: string;
  nationality?: string;
  location?: string;
  age?: number;
  gender?: string;
  profilePhoto?: string;
  profileImageUrl?: string;
  currentLevel: number;
  createdAt: string;
  membershipDate?: string;
  lastLoginAt: string;
  completedLessons: number;
  completedQuizzes: number;
  averageScore: number;
  overallProgress: number;
  /** IANA time zone id (e.g. Europe/Berlin); used for daily goal boundaries and future scheduling. */
  timeZone?: string | null;
  /** Target focused study minutes per calendar day in your time zone (5–480). */
  dailyLearningGoalMinutes?: number;
  emailNotifications?: boolean;
  pushNotifications?: boolean;
}

// Server response interface (what the API actually returns)
interface ServerUserProfileDto {
  id: string;
  email: string;
  userName?: string;
  phoneNumber?: string;
  phoneNumberConfirmed?: boolean;
  firstName?: string;
  lastName?: string;
  bio?: string;
  nationality?: string;
  location?: string;
  age?: number;
  gender?: string;
  profileImageUrl?: string; // Server uses this field name
  profilePhoto?: string; // Fallback in case server uses this name
  currentLevel: number;
  createdAt: string;
  membershipDate?: string;
  lastLoginAt: string;
  completedLessons: number;
  completedQuizzes: number;
  averageScore: number;
  overallProgress: number;
  timeZone?: string | null;
  dailyLearningGoalMinutes?: number;
}

export interface AuthResponseDto {
  success: boolean;
  message?: string;
  messageKey?: string;
  lockoutMinutesRemaining?: number;
  token?: string;
  expiresAt?: string;
  user?: UserProfileDto;
  requiresTwoFactor?: boolean;
  requiresTwoFactorSetup?: boolean;
  twoFactorToken?: string;
  twoFactorMethod?: string;
  twoFactorDeliveryHint?: string;
  twoFactorAccountEmailHint?: string;
  requiresCaptcha?: boolean;
  captchaError?: boolean;
}

export type { TwoFactorStatusDto, TwoFactorMethodKind } from './twoFactorService';

export interface TwoFactorSetupDto {
  sharedKey: string;
  authenticatorUri: string;
}

export interface TwoFactorEnableResponseDto {
  success: boolean;
  message: string;
  recoveryCodes: string[];
}

export interface TwoFactorLoginCompleteSetupResponse {
  success: boolean;
  message: string;
  recoveryCodes: string[];
  login: AuthResponseDto;
}

export interface UpdateProfileDto {
  email?: string;
  userName?: string;
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
  bio?: string;
  nationality?: string;
  age?: number;
  gender?: string;
  profilePhoto?: string;
}

export interface ChangePasswordDto {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword?: string;
}

/** Matches server LearningPreferencesDto (camelCase JSON). */
export interface LearningPreferencesDto {
  learningGoals: string;
  preferredLearningStyle: string;
  dailyLearningGoalMinutes: number;
  emailNotifications: boolean;
  pushNotifications: boolean;
  preferredLanguage: string;
  timeZone: string;
  /** Lesson Text / Text+Audio: system | vazir | nazanin | koodak | mitra. Empty = not saved on account yet. */
  preferredPersianFontId: string;
  /** Lesson Text / Text+Audio body size scale (0.35–3). */
  lessonTextFontMultiplier: number;
  /** True after the learner has saved a daily time target from Goals & Time. */
  hasSetDailyLearningGoal: boolean;
}

function coerceLearningPreferences(raw: unknown): LearningPreferencesDto {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const timeZoneRaw = o.timeZone ?? o.TimeZone;
  const fontRaw = o.preferredPersianFontId ?? o.PreferredPersianFontId;
  const fontStr = typeof fontRaw === 'string' ? fontRaw.trim().toLowerCase() : '';
  const preferredPersianFontId =
    fontStr === 'system' || fontStr === 'vazir' || fontStr === 'nazanin' || fontStr === 'koodak' || fontStr === 'mitra' ? fontStr : '';
  const multRaw = o.lessonTextFontMultiplier ?? o.LessonTextFontMultiplier;
  const multNum = typeof multRaw === 'number' ? multRaw : parseFloat(String(multRaw ?? ''));
  const lessonTextFontMultiplier = (() => {
    if (!Number.isFinite(multNum)) return 1;
    return Math.min(3, Math.max(0.35, multNum));
  })();
  const browserTz = typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC';
  const daily = Number(o.dailyLearningGoalMinutes ?? o.DailyLearningGoalMinutes ?? 30);
  const clampedDaily = Math.min(480, Math.max(5, Number.isFinite(daily) ? daily : 30));
  return {
    learningGoals: String(o.learningGoals ?? o.LearningGoals ?? ''),
    preferredLearningStyle: String(o.preferredLearningStyle ?? o.PreferredLearningStyle ?? 'Visual'),
    dailyLearningGoalMinutes: clampedDaily,
    emailNotifications: Boolean(o.emailNotifications ?? o.EmailNotifications ?? true),
    pushNotifications: Boolean(o.pushNotifications ?? o.PushNotifications ?? false),
    preferredLanguage: String(o.preferredLanguage ?? o.PreferredLanguage ?? 'en'),
    timeZone: typeof timeZoneRaw === 'string' && timeZoneRaw.trim() ? timeZoneRaw.trim() : browserTz,
    preferredPersianFontId,
    lessonTextFontMultiplier,
    hasSetDailyLearningGoal: Boolean(o.hasSetDailyLearningGoal ?? o.HasSetDailyLearningGoal)
  };
}

export interface TokenInfo {
  token: string;
  expiresAt: string;
  issuedAt: string;
  userId: string;
  roles: string[];
}

export interface AuthError extends Error {
  statusCode: number;
  details?: string;
}

/** Learner achievement progress (from GET /api/UserProfile/achievement-progress) */
export interface AchievementProgressDto {
  id: number;
  name: string;
  description: string;
  iconUrl?: string | null;
  type: string;
  /** Admin-defined category for grouping (e.g. Onboarding, Learning). */
  category?: string;
  requiredValue: number;
  currentValue: number;
  xpReward: number;
  isEarned: boolean;
  earnedAt?: string | null;
  isNew?: boolean;
  progressPercentage?: number;
  displayOrder: number;
}

class AuthService {
  private refreshTokenPromise: Promise<string> | null = null;
  private tokenExpirationTimer: NodeJS.Timeout | null = null;

  constructor() {
    this.setupTokenExpirationCheck();
    this.setupUnauthorizedHandler();
    this.setupVisibilityRefresh();

    // Re-arm refresh timer after page reload if a session is still stored
    const existingToken = localStorage.getItem('authToken');
    if (existingToken) {
      const info = this.parseJwtToken(existingToken);
      if (info) {
        localStorage.setItem('authTokenInfo', JSON.stringify(info));
        this.scheduleTokenRefresh(info);
      }
    }
  }

  /**
   * Maps server response to client interface
   * Handles field name differences between server and client
   */
  private mapServerProfileToClient(serverProfile: ServerUserProfileDto): UserProfileDto {
    const { profileImageUrl, profilePhoto: serverProfilePhoto, ...rest } = serverProfile;

    // Get photo URL from database field
    let profilePhoto = profileImageUrl || serverProfilePhoto;
    
    // Convert /uploads/profiles/filename to full image endpoint URL
    if (profilePhoto) {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5127';
      
      if (profilePhoto.startsWith('/uploads/profiles/') || profilePhoto.startsWith('uploads/profiles/')) {
        const filename = profilePhoto.split('/').pop();
        if (filename) {
          profilePhoto = `${baseUrl}/api/UserProfile/image/${filename}`;
        }
      } else if (profilePhoto.startsWith('/api/UserProfile/image/') && !profilePhoto.startsWith('http')) {
        profilePhoto = `${baseUrl}${profilePhoto}`;
      } else if (!profilePhoto.startsWith('http') && !profilePhoto.startsWith('data:')) {
        profilePhoto = `${baseUrl}${profilePhoto.startsWith('/') ? profilePhoto : '/' + profilePhoto}`;
      }
    }

    const mapped: UserProfileDto = {
      ...rest,
      phoneNumberConfirmed: Boolean(
        (serverProfile as ServerUserProfileDto & { phoneNumberConfirmed?: boolean; PhoneNumberConfirmed?: boolean })
          .phoneNumberConfirmed
          ?? (serverProfile as { PhoneNumberConfirmed?: boolean }).PhoneNumberConfirmed
      )
    };
    if (profilePhoto) {
      mapped.profilePhoto = profilePhoto;
    }
    const nationality = serverProfile.nationality ?? serverProfile.location;
    if (nationality) {
      mapped.nationality = nationality;
      mapped.location = nationality;
    }

    return mapped;
  }

  private setupTokenExpirationCheck() {
    // Removed proactive session validation that causes unexpected logouts
    // Session validation now only happens on actual user actions (API calls, navigation)
    console.log('AuthService: Proactive session validation disabled - validation only on user actions');
  }

  /** Full URL to start Google OAuth (browser redirect). */
  buildGoogleOAuthUrl(context: 'register' | 'login'): string {
    return this.buildExternalOAuthUrl('google', context);
  }

  buildExternalOAuthUrl(provider: 'google' | 'microsoft' | 'apple' | 'yahoo', context: 'register' | 'login'): string {
    const apiOrigin = this.getOAuthApiOrigin();
    const returnUrl = encodeURIComponent(
      `${window.location.origin}${window.location.pathname}${window.location.search}`
    );
    const route =
      provider === 'google'
        ? 'google-login'
        : provider === 'microsoft'
          ? 'microsoft-login'
          : provider === 'apple'
            ? 'apple-login'
            : 'yahoo-login';
    return `${apiOrigin}/api/Auth/${route}?returnUrl=${returnUrl}&context=${context}`;
  }

  /** API host for full-page OAuth redirects (respects localhost vs production like apiService). */
  private getOAuthApiOrigin(): string {
    let base = apiConfig.getBaseUrl().trim().replace(/\/$/, '');
    if (/\/api$/i.test(base)) {
      base = base.replace(/\/api$/i, '');
    }
    return base || 'http://localhost:5127';
  }

  async getExternalAuthProviders(): Promise<{
    google: boolean;
    microsoft: boolean;
    apple: boolean;
    yahoo: boolean;
  }> {
    try {
      const r = await apiService.get<{
        google?: boolean;
        microsoft?: boolean;
        apple?: boolean;
        yahoo?: boolean;
        Google?: boolean;
        Microsoft?: boolean;
        Apple?: boolean;
        Yahoo?: boolean;
      }>('/api/Auth/external-auth-providers', { cache: false });
      const body = (r as { data?: typeof r })?.data ?? r;
      return {
        google: body?.google === true || body?.Google === true,
        microsoft: body?.microsoft === true || body?.Microsoft === true,
        apple: body?.apple === true || body?.Apple === true,
        yahoo: body?.yahoo === true || body?.Yahoo === true
      };
    } catch {
      return { google: false, microsoft: false, apple: false, yahoo: false };
    }
  }

  async isGoogleAuthConfigured(): Promise<boolean> {
    const p = await this.getExternalAuthProviders();
    return p.google;
  }

  /** Parse OAuth fragment without removing it (safe under React Strict Mode). */
  parseGoogleOAuthFragment(): {
    token: string;
    oauth_context: string;
    isNewUser: boolean;
  } | null {
    const hash = window.location.hash;
    if (!hash?.includes('access_token=')) return null;
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const token = params.get('access_token');
    if (!token) return null;
    return {
      token,
      oauth_context: params.get('oauth_context') || 'login',
      isNewUser: params.get('is_new_user') === '1'
    };
  }

  /** Remove OAuth tokens from the URL after successful session setup. */
  clearGoogleOAuthFragment(): void {
    this.clearOAuthFragment();
  }

  /** Store JWT and profile after password login or 2FA completion. */
  finishLoginFromResponse(response: AuthResponseDto): UserProfileDto | undefined {
    if (!response.success || !response.token) {
      return undefined;
    }
    this.applyOAuthToken(response.token);
    let mappedUser: UserProfileDto | undefined;
    if (response.user) {
      mappedUser = response.user.profileImageUrl
        ? this.mapServerProfileToClient(response.user as ServerUserProfileDto)
        : response.user;
      localStorage.setItem('userProfile', JSON.stringify(mappedUser));
    }
    this.syncAuthSession(mappedUser);
    return mappedUser;
  }

  /** Persist JWT after Google redirect (same as login). */
  applyOAuthToken(token: string): void {
    this.setTokenWithExpiration(token);
    try {
      void import('../utils/sessionInactivityTracker').then(({ sessionInactivityTracker }) => {
        sessionInactivityTracker.recordActivity();
      });
    } catch {
      /* ignore */
    }
  }

  private setupUnauthorizedHandler() {
    // Listen for unauthorized events from API service
    window.addEventListener('auth:unauthorized', (event: any) => {
      // CRITICAL: Check if redirect is already in progress to prevent page freezing
      if ((window as any).__sessionRedirectInProgress) {
        console.log('AuthService: Redirect already in progress, ignoring unauthorized event');
        return;
      }

      if ((window as any).__intentionalLogoutInProgress) {
        console.log('AuthService: Intentional logout in progress, ignoring unauthorized event');
        return;
      }

      console.log('AuthService: Unauthorized event received');

      const hadAdminToken = !!localStorage.getItem('adminToken');

      void Promise.all([
        import('../utils/sessionExpirationPolicy'),
        import('../utils/sessionExpiredRedirect'),
      ]).then(([policy, redirect]) => {
        const target = redirect.resolveSessionExpiredTarget({ hadAdminToken });
        if (target === 'admin') {
          void policy.redirectAdminForSecurity('session_expired');
          return;
        }
        policy.redirectLearnerForSecurity();
      });
    });

    // Listen for session expired events
    window.addEventListener('session:expired', (event: any) => {
      // CRITICAL: Check if redirect is already in progress to prevent page freezing
      if ((window as any).__sessionRedirectInProgress) {
        console.log('AuthService: Redirect already in progress, ignoring session expired event');
        return;
      }

      if ((window as any).__intentionalLogoutInProgress) {
        console.log('AuthService: Intentional logout in progress, ignoring session expired event');
        return;
      }

      console.log('AuthService: Session expired event received', event.detail);
      const hadAdminToken = !!localStorage.getItem('adminToken');

      void Promise.all([
        import('../utils/sessionExpirationPolicy'),
        import('../utils/sessionExpiredRedirect'),
      ]).then(([policy, redirect]) => {
        const target = redirect.resolveSessionExpiredTarget({ hadAdminToken });
        if (target === 'admin') {
          void policy.redirectAdminForSecurity('session_expired');
          return;
        }
        policy.redirectLearnerForSecurity();
      });
    });
  }

  /**
   * Show simple sliding notification for session expiration
   * Replaces countdown timers with simple user-friendly messaging
   */
  private showSessionExpiredNotification(message?: string) {
    try {
      // Dispatch a custom event that the notification system can listen to
      window.dispatchEvent(new CustomEvent('session:expired:notification', {
        detail: {
          message: message || undefined,
          type: 'warning',
          persistent: false,
          duration: 5000,
          context: message ? 'session_expiration_custom' : 'session_inactivity_expiration'
        }
      }));
    } catch (error) {
      console.warn('AuthService: Failed to show session expired notification:', error);
    }
  }

  private parseJwtToken(token: string): TokenInfo | null {
    try {
      if (!token || typeof token !== 'string') {
        return null;
      }

      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const payload = JSON.parse(atob(parts[1]));
      return {
        token,
        expiresAt: new Date(payload.exp * 1000).toISOString(),
        issuedAt: new Date(payload.iat * 1000).toISOString(),
        userId: payload.sub || payload.userId,
        roles: payload.roles || (payload.role ? [payload.role] : []),
      };
    } catch {
      return null;
    }
  }

  private isTokenExpiringSoon(token?: string): boolean {
    const tokenToCheck = token || localStorage.getItem('authToken');
    if (!tokenToCheck) return false;

    const tokenInfo = this.parseJwtToken(tokenToCheck);
    if (!tokenInfo) return true;

    const expirationTime = new Date(tokenInfo.expiresAt).getTime();
    const currentTime = Date.now();
    const fiveMinutesInMs = 5 * 60 * 1000;

    return expirationTime - currentTime < fiveMinutesInMs;
  }

  /** Notify AuthProvider and other listeners that the session user changed (login/register/OAuth). */
  syncAuthSession(user?: UserProfileDto | null): void {
    const resolved = user ?? this.getCurrentUser();
    window.dispatchEvent(new CustomEvent('auth:login', { detail: { user: resolved } }));
  }

  private notifyAuthLogin(user?: UserProfileDto | null): void {
    this.syncAuthSession(user);
  }

  private setTokenWithExpiration(token: string) {
    const tokenInfo = this.parseJwtToken(token);
    if (!tokenInfo) {
      throw new Error('Invalid token format');
    }

    apiService.setToken(token);
    localStorage.setItem('authToken', token);
    localStorage.setItem('authTokenInfo', JSON.stringify(tokenInfo));

    this.scheduleTokenRefresh(tokenInfo);
  }

  /** Refresh shortly before JWT expiry; if already due, refresh immediately. */
  private scheduleTokenRefresh(tokenInfo: TokenInfo): void {
    const expirationTime = new Date(tokenInfo.expiresAt).getTime();
    const refreshLeadMs = 5 * 60 * 1000;
    const refreshDelay = Math.max(0, expirationTime - Date.now() - refreshLeadMs);

    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
      this.tokenExpirationTimer = null;
    }

    const runRefresh = () => {
      this.refreshToken().catch((err: unknown) => {
        void import('../utils/sessionExpirationPolicy').then(({ isSecurityRejection, redirectLearnerForSecurity }) => {
          if (isSecurityRejection(err)) {
            redirectLearnerForSecurity();
          }
        });
      });
    };

    if (refreshDelay === 0) {
      runRefresh();
      return;
    }

    this.tokenExpirationTimer = setTimeout(runRefresh, refreshDelay);
  }

  /** When a background tab wakes, renew JWT if it expired or is about to. */
  private setupVisibilityRefresh(): void {
    if (typeof document === 'undefined') return;

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!localStorage.getItem('authToken')) return;
      if (this.isTokenExpiringSoon() || this.isTokenExpired()) {
        this.refreshToken().catch(() => {
          /* network / transient — keep stored session for next attempt */
        });
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
  }

  async login(credentials: LoginDto): Promise<AuthResponseDto> {
    const loginStartTime = Date.now();
    
    try {
      console.log('AuthService: Sending login request with credentials:', credentials);
      console.log('AuthService: API Base URL:', import.meta.env.VITE_API_BASE_URL);

      // Add timeout to the request - increased for better reliability
      const response = await apiService.post<AuthResponseDto>('/api/Auth/login', credentials, {
        timeout: 15000,
        retries: 0,
      });
      console.log('AuthService: Received login response:', response);

      if (response.success && response.token) {
        console.log('AuthService: Login successful, setting token and storing user profile');
        this.setTokenWithExpiration(response.token);

        // Record activity for session tracking
        try {
          const { sessionInactivityTracker } = await import('../utils/sessionInactivityTracker');
          sessionInactivityTracker.recordActivity();
          (window as any).__sessionRedirectInProgress = false;
          console.log('AuthService: Activity recorded after login');
        } catch (error) {
          console.warn('AuthService: Failed to record activity after login:', error);
        }

        // Store user profile if provided in login response
        let mappedUser: UserProfileDto | undefined;
        if (response.user) {
          // Map server user to client format if needed
          mappedUser = response.user.profileImageUrl ?
            this.mapServerProfileToClient(response.user as any) :
            response.user;
          localStorage.setItem('userProfile', JSON.stringify(mappedUser));
          console.log('AuthService: User profile from login stored in localStorage');
        }

        this.notifyAuthLogin(mappedUser);

        // Skip fetching complete profile immediately after login to improve performance
        // The profile from login response is already stored in localStorage
        // Full profile can be fetched on-demand when user navigates to profile page

        // Log successful login action
        try {
          const loginDuration = Date.now() - loginStartTime;
          await userActionLogger.logLogin(
            response.user?.id || 'unknown',
            'email',
            {
              rememberMe: credentials.rememberMe || false,
              loginDuration,
              userAgent: navigator.userAgent,
              timestamp: new Date().toISOString()
            }
          );
        } catch (logError) {
          console.warn('AuthService: Failed to log login action:', logError);
          // Don't fail login if logging fails
        }
      } else {
        console.log('AuthService: Login failed - no success flag or token');
        
        // Log failed login attempt
        try {
          await userActionLogger.logFailedLogin(
            credentials.email,
            'invalid_credentials',
            {
              rememberMe: credentials.rememberMe || false,
              userAgent: navigator.userAgent
            }
          );
        } catch (logError) {
          console.warn('AuthService: Failed to log failed login attempt:', logError);
        }
      }

      return response;
    } catch (error) {
      console.error('AuthService: Login error:', error);
      console.error('AuthService: Error details:', {
        message: error.message,
        statusCode: error.statusCode,
        stack: error.stack
      });

      // Log login error - determine specific failure reason
      try {
        let failureReason = 'invalid_credentials';
        if (error.statusCode === 401) {
          // Check if it's a wrong password (user exists but password is wrong)
          // vs user not found (both return 401 for security)
          failureReason = 'wrong_password';
        } else if (error.statusCode === 404) {
          failureReason = 'user_not_found';
        } else if (error.statusCode === 423) {
          failureReason = 'account_locked';
        } else if (error.message?.toLowerCase().includes('network') || 
                   error.message?.toLowerCase().includes('fetch') ||
                   error.message?.toLowerCase().includes('timeout') ||
                   error.message?.toLowerCase().includes('connection')) {
          failureReason = 'network_error';
        }

        await userActionLogger.logFailedLogin(
          credentials.email,
          failureReason as any,
          {
            error: error.message,
            statusCode: error.statusCode,
            rememberMe: credentials.rememberMe || false,
            isNetworkError: failureReason === 'network_error'
          }
        );
      } catch (logError) {
        console.warn('AuthService: Failed to log login error:', logError);
      }

      if (error.statusCode === 401 || error.statusCode === 400) {
        const lockout = extractLockoutMessageFields(error);
        return {
          success: false,
          message: extractAuthErrorMessage(
            error,
            'Invalid email or password. Please check your credentials and try again.'
          ),
          messageKey: lockout.messageKey,
          lockoutMinutesRemaining: lockout.lockoutMinutesRemaining,
          requiresCaptcha: extractRequiresCaptcha(error),
          captchaError: extractCaptchaError(error),
        };
      }

      return {
        success: false,
        message: extractAuthErrorMessage(
          error,
          'An unexpected error occurred during sign-in. Please try again later.'
        ),
        requiresCaptcha: extractRequiresCaptcha(error),
        captchaError: extractCaptchaError(error),
      };
    }
  }

  async completeTwoFactorLogin(twoFactorToken: string, code: string): Promise<AuthResponseDto> {
    const response = await apiService.post<AuthResponseDto>('/api/Auth/two-factor/login', {
      twoFactorToken,
      code
    });
    if (response.success && response.token) {
      this.setTokenWithExpiration(response.token);
      let mappedUser: UserProfileDto | undefined;
      if (response.user) {
        mappedUser = response.user.profileImageUrl
          ? this.mapServerProfileToClient(response.user as ServerUserProfileDto)
          : response.user;
        localStorage.setItem('userProfile', JSON.stringify(mappedUser));
      }
      this.notifyAuthLogin(mappedUser);
    }
    return response;
  }

  async completeTwoFactorSetupLogin(
    twoFactorToken: string,
    code: string
  ): Promise<TwoFactorLoginCompleteSetupResponse> {
    const response = await apiService.post<TwoFactorLoginCompleteSetupResponse>(
      '/api/Auth/two-factor/complete-setup-login',
      { twoFactorToken, code }
    );
    if (response.login?.success && response.login.token) {
      this.setTokenWithExpiration(response.login.token);
      if (response.login.user) {
        const mappedUser = response.login.user.profileImageUrl
          ? this.mapServerProfileToClient(response.login.user as ServerUserProfileDto)
          : response.login.user;
        localStorage.setItem('userProfile', JSON.stringify(mappedUser));
        this.notifyAuthLogin(mappedUser);
      }
    }
    return response;
  }

  async getTwoFactorStatus(): Promise<TwoFactorStatusDto> {
    return apiService.get<TwoFactorStatusDto>('/api/Auth/two-factor/status');
  }

  async setupTwoFactor(reset = false): Promise<TwoFactorSetupDto> {
    const q = reset ? '?reset=true' : '';
    return apiService.post<TwoFactorSetupDto>(`/api/Auth/two-factor/setup${q}`, {});
  }

  async setupTwoFactorLogin(twoFactorToken: string): Promise<TwoFactorSetupDto> {
    return apiService.post<TwoFactorSetupDto>('/api/Auth/two-factor/setup-login', { twoFactorToken });
  }

  async enableTwoFactor(code: string): Promise<TwoFactorEnableResponseDto> {
    return apiService.post<TwoFactorEnableResponseDto>('/api/Auth/two-factor/enable', { code });
  }

  async disableTwoFactor(code: string, password?: string | null): Promise<AuthResponseDto> {
    const body: { code: string; password?: string } = { code };
    const trimmedPassword = password?.trim();
    if (trimmedPassword) {
      body.password = trimmedPassword;
    }
    return apiService.post<AuthResponseDto>('/api/Auth/two-factor/disable', body);
  }

  /** OAuth redirect when 2FA is required before issuing JWT. */
  parseGoogleOAuthTwoFactorFragment(): {
    twoFactorToken: string;
    requiresTwoFactor: boolean;
    requiresTwoFactorSetup: boolean;
    oauth_context: string;
    twoFactorMethod: string;
  } | null {
    return this.parseOAuthTwoFactorFragment();
  }

  parseOAuthTwoFactorFragment(): {
    twoFactorToken: string;
    requiresTwoFactor: boolean;
    requiresTwoFactorSetup: boolean;
    oauth_context: string;
    twoFactorMethod: string;
    twoFactorEmailHint: string;
  } | null {
    const hash = window.location.hash;
    if (!hash?.includes('two_factor_token=')) return null;
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const twoFactorToken = params.get('two_factor_token');
    if (!twoFactorToken) return null;
    return {
      twoFactorToken,
      requiresTwoFactor: params.get('requires_two_factor') === '1',
      requiresTwoFactorSetup: params.get('requires_two_factor_setup') === '1',
      oauth_context: params.get('oauth_context') || 'login',
      twoFactorMethod: params.get('two_factor_method') || 'authenticator',
      twoFactorEmailHint: params.get('two_factor_email_hint') || ''
    };
  }

  clearOAuthFragment(): void {
    if (!window.location.hash) return;
    const h = window.location.hash;
    if (
      h.includes('access_token=') ||
      h.includes('two_factor_token=') ||
      h.includes('oauth_error')
    ) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  async register(userData: RegisterDto): Promise<AuthResponseDto> {
    try {
      const response = await apiService.post<AuthResponseDto>('/api/Auth/register', userData);

      if (response.success && response.token) {
        this.setTokenWithExpiration(response.token);

        // Store user profile if provided
        let mappedUser: UserProfileDto | undefined;
        if (response.user) {
          // Map server user to client format if needed
          mappedUser = response.user.profileImageUrl ?
            this.mapServerProfileToClient(response.user as any) :
            response.user;
          localStorage.setItem('userProfile', JSON.stringify(mappedUser));
        }

        this.notifyAuthLogin(mappedUser);

        // Log successful registration
        try {
          const userId = response.user?.id || `temp_${Date.now()}`;
          await userActionLogger.logSignup(
            userId,
            userData.email,
            'email',
            {
              hasConfirmPassword: !!userData.confirmPassword,
              userAgent: navigator.userAgent,
              registrationSuccess: true
            }
          );
        } catch (logError) {
          console.warn('AuthService: Failed to log registration action:', logError);
        }
      } else {
        // Log failed registration
        try {
          await userActionLogger.logFailedSignup(
            userData.email,
            'registration_failed',
            undefined,
            {
              hasConfirmPassword: !!userData.confirmPassword,
              userAgent: navigator.userAgent,
              registrationSuccess: false
            }
          );
        } catch (logError) {
          console.warn('AuthService: Failed to log failed registration:', logError);
        }
      }

      return response;
    } catch (error) {
      // Log registration error
      try {
        let failureReason = 'registration_error';
        if (error.statusCode === 409) {
          failureReason = 'email_already_exists';
        } else if (error.statusCode === 400) {
          failureReason = 'validation_failed';
        }

        await userActionLogger.logFailedSignup(
          userData.email,
          failureReason,
          error.validationErrors || [],
          {
            error: error.message,
            statusCode: error.statusCode,
            hasConfirmPassword: !!userData.confirmPassword
          }
        );
      } catch (logError) {
        console.warn('AuthService: Failed to log registration error:', logError);
      }

      throw this.handleAuthError(error);
    }
  }

  /**
   * Send email verification code
   */
  async sendEmailVerificationCode(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiService.post<{ success: boolean; message: string }>('/api/Auth/send-email-verification', { email });
      return response;
    } catch (error) {
      // If API is not available, create a mock response for development
      if (import.meta.env.DEV && error.statusCode === 404) {
        console.warn('Email verification API not available, using mock response');
        // Generate a mock verification code for development
        const mockCode = Math.floor(100000 + Math.random() * 900000).toString();
        localStorage.setItem(`emailVerificationCode_${email}`, mockCode);
        console.log(`Mock email verification code for ${email}: ${mockCode}`);
        return {
          success: true,
          message: `Verification code sent to ${email}. For development: ${mockCode}`
        };
      }
      throw this.handleAuthError(error);
    }
  }

  /**
   * Verify email with verification code
   */
  async verifyEmail(verificationData: EmailVerificationDto): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiService.post<{ success: boolean; message: string }>('/api/Auth/verify-email', verificationData);
      return response;
    } catch (error) {
      // If API is not available, create a mock response for development
      if (import.meta.env.DEV && error.statusCode === 404) {
        console.warn('Email verification API not available, using mock verification');
        const storedCode = localStorage.getItem(`emailVerificationCode_${verificationData.email}`);
        if (storedCode === verificationData.verificationCode) {
          localStorage.removeItem(`emailVerificationCode_${verificationData.email}`);
          return {
            success: true,
            message: 'Email verified successfully'
          };
        } else {
          return {
            success: false,
            message: 'Invalid verification code'
          };
        }
      }
      throw this.handleAuthError(error);
    }
  }

  async checkPhoneAvailability(phoneNumber: string): Promise<PhoneAvailabilityDto> {
    const raw = await apiService.get<Record<string, unknown>>(
      `/api/Verification/phone/availability?phone=${encodeURIComponent(phoneNumber)}`,
      { cache: false }
    );
    return {
      isAvailable: Boolean(raw.isAvailable ?? raw.IsAvailable),
      isValid: raw.isValid === undefined && raw.IsValid === undefined ? true : Boolean(raw.isValid ?? raw.IsValid),
      phoneNumber: String(raw.phoneNumber ?? raw.PhoneNumber ?? phoneNumber),
      message: String(raw.message ?? raw.Message ?? ''),
      messageKey: (raw.messageKey ?? raw.MessageKey) as string | undefined,
      errorCode: (raw.errorCode ?? raw.ErrorCode) as string | undefined
    };
  }

  /**
   * Send a phone verification code to the linked Telegram bot.
   */
  async sendPhoneVerificationCode(phoneNumber: string): Promise<PhoneVerificationSendResultDto> {
    const raw = await apiService.post<Record<string, unknown>>('/api/Verification/phone/send', {
      newPhoneNumber: phoneNumber
    });
    return this.normalizePhoneSendResult(raw);
  }

  /**
   * Verify the Telegram phone code and save the number.
   */
  async verifyPhone(verificationData: PhoneVerificationDto): Promise<PhoneVerificationConfirmResultDto> {
    const raw = await apiService.post<Record<string, unknown>>('/api/Verification/phone/verify', {
      code: verificationData.verificationCode
    });
    const profileRaw = (raw.profile ?? raw.Profile) as ServerUserProfileDto | undefined;
    return {
      success: Boolean(raw.success ?? raw.Success),
      message: String(raw.message ?? raw.Message ?? ''),
      messageKey: (raw.messageKey ?? raw.MessageKey) as string | undefined,
      errorCode: (raw.errorCode ?? raw.ErrorCode) as string | undefined,
      profile: profileRaw ? this.mapServerProfileToClient(profileRaw) : undefined
    };
  }

  private normalizePhoneSendResult(raw: Record<string, unknown>): PhoneVerificationSendResultDto {
    return {
      success: Boolean(raw.success ?? raw.Success),
      message: String(raw.message ?? raw.Message ?? ''),
      messageKey: (raw.messageKey ?? raw.MessageKey) as string | undefined,
      errorCode: (raw.errorCode ?? raw.ErrorCode) as string | undefined,
      telegramLinked: Boolean(raw.telegramLinked ?? raw.TelegramLinked),
      cooldownSeconds: Number(raw.cooldownSeconds ?? raw.CooldownSeconds ?? 0),
      expiresAt: (raw.expiresAt ?? raw.ExpiresAt) as string | null | undefined,
      developmentCode: (raw.developmentCode ?? raw.DevelopmentCode) as string | null | undefined
    };
  }

  async getProfile(forceRefresh: boolean = true): Promise<UserProfileDto> {
    try {
      // Always bypass cache to get fresh data from DB
      // Try the UserProfile endpoint first, fallback to Auth endpoint
      let serverProfile: ServerUserProfileDto;
      try {
        serverProfile = await apiService.get<ServerUserProfileDto>('/api/UserProfile/profile', {
          cache: false
        });
      } catch (userProfileError) {
        // Fallback to Auth endpoint
        serverProfile = await apiService.get<ServerUserProfileDto>('/api/Auth/profile', {
          cache: false
        });
      }

      // Map server response to client interface
      const profile = this.mapServerProfileToClient(serverProfile);

      // Update cached profile
      localStorage.setItem('userProfile', JSON.stringify(profile));

      return profile;
    } catch (error) {
      // If API is not available, return cached profile for development
      if (import.meta.env.DEV && error.statusCode === 404) {
        console.warn('Profile API not available, using cached profile');

        const cachedProfile = this.getCurrentUser();
        if (cachedProfile) {
          console.log('Returning cached profile with photo:', cachedProfile.profilePhoto ? 'YES' : 'NO');
          return cachedProfile;
        }

        // If no cached profile, create a default one only if we really need to
        console.warn('No cached profile found, creating default profile');
        const defaultProfile: UserProfileDto = {
          id: '1',
          email: 'user@example.com',
          firstName: '',
          lastName: '',
          phoneNumber: '',
          profilePhoto: '',
          createdAt: new Date().toISOString(),
          lastLoginAt: new Date().toISOString(),
          currentLevel: 1,
          completedLessons: 0,
          completedQuizzes: 0,
          averageScore: 0,
          overallProgress: 0
        };

        localStorage.setItem('userProfile', JSON.stringify(defaultProfile));
        return defaultProfile;
      }

      throw this.handleAuthError(error);
    }
  }

  async getAchievementProgress(): Promise<AchievementProgressDto[]> {
    const response = await apiService.get<AchievementProgressDto[]>('/api/UserProfile/achievement-progress', { cache: false });
    return response ?? [];
  }

  /** Mark achievements as notified so "achievement unlocked" is not shown again. */
  async markAchievementsNotified(achievementIds: number[]): Promise<void> {
    if (!achievementIds.length) return;
    await apiService.post('/api/UserProfile/achievements/mark-notified', achievementIds);
  }

  async getLearningPreferences(): Promise<LearningPreferencesDto> {
    const raw = await apiService.get<unknown>('/api/UserProfile/learning-preferences', { cache: false });
    return coerceLearningPreferences(raw);
  }

  async updateLearningPreferences(prefs: LearningPreferencesDto): Promise<LearningPreferencesDto> {
    const raw = await apiService.put<unknown>('/api/UserProfile/learning-preferences', prefs);
    return coerceLearningPreferences(raw);
  }

  async updateProfile(profileData: UpdateProfileDto): Promise<UserProfileDto> {
    const currentProfile = this.getCurrentUser();
    
    try {
      // Try the UserProfile endpoint first, fallback to Auth endpoint
      let serverProfile: ServerUserProfileDto;
      try {
        serverProfile = await apiService.put<ServerUserProfileDto>('/api/UserProfile/profile', profileData);
      } catch (userProfileError) {
        // Fallback to Auth endpoint
        serverProfile = await apiService.put<ServerUserProfileDto>('/api/Auth/profile', profileData);
      }

      // Map server response to client interface
      const updatedProfile = this.mapServerProfileToClient(serverProfile);

      // Update cached profile
      localStorage.setItem('userProfile', JSON.stringify(updatedProfile));

      // Log profile update action
      try {
        await userActionLogger.logUserAction({
          type: 'update_profile',
          resourceType: 'user_profile',
          resourceId: updatedProfile.id,
          userId: updatedProfile.id,
          sessionId: userActionLogger['getSessionId']?.() || 'unknown',
          ipAddress: await userActionLogger['getClientIP']?.() || 'unknown',
          userAgent: navigator.userAgent,
          previousValue: currentProfile,
          newValue: updatedProfile,
          details: {
            updatedFields: Object.keys(profileData),
            timestamp: new Date().toISOString()
          }
        }, {
          component: 'AuthService',
          feature: 'profile_management'
        });
      } catch (logError) {
        console.warn('AuthService: Failed to log profile update:', logError);
      }

      return updatedProfile;
    } catch (error) {
      // If API is not available, create a mock response for development
      if (import.meta.env.DEV && error.statusCode === 404) {
        console.warn('Profile update API not available, using mock response');

        if (currentProfile) {
          console.log('Current profile before update:', currentProfile);
          console.log('Profile data to merge:', profileData);

          // Ensure we preserve the photo and other important fields
          const mockUpdatedProfile: UserProfileDto = {
            // Start with current profile
            ...currentProfile,
            // Apply updates
            ...profileData,
            // Explicitly preserve important fields that shouldn't be overwritten
            id: currentProfile.id,
            createdAt: currentProfile.createdAt,
            lastLoginAt: new Date().toISOString(),
            currentLevel: currentProfile.currentLevel,
            completedLessons: currentProfile.completedLessons,
            completedQuizzes: currentProfile.completedQuizzes,
            averageScore: currentProfile.averageScore,
            overallProgress: currentProfile.overallProgress,
            // Ensure required fields have values
            email: profileData.email || currentProfile.email,
            firstName: profileData.firstName || currentProfile.firstName || '',
            lastName: profileData.lastName || currentProfile.lastName || '',
            phoneNumber: profileData.phoneNumber || currentProfile.phoneNumber || ''
          };
          if (profileData.profilePhoto || currentProfile.profilePhoto) {
            mockUpdatedProfile.profilePhoto = profileData.profilePhoto || currentProfile.profilePhoto;
          }

          console.log('Merged profile:', mockUpdatedProfile);

          // Update cached profile
          localStorage.setItem('userProfile', JSON.stringify(mockUpdatedProfile));

          // Log mock profile update
          try {
            await userActionLogger.logUserAction({
              type: 'update_profile',
              resourceType: 'user_profile',
              resourceId: mockUpdatedProfile.id,
              userId: mockUpdatedProfile.id,
              sessionId: userActionLogger['getSessionId']?.() || 'unknown',
              ipAddress: await userActionLogger['getClientIP']?.() || 'unknown',
              userAgent: navigator.userAgent,
              previousValue: currentProfile,
              newValue: mockUpdatedProfile,
              details: {
                updatedFields: Object.keys(profileData),
                mockUpdate: true,
                timestamp: new Date().toISOString()
              }
            }, {
              component: 'AuthService',
              feature: 'profile_management'
            });
          } catch (logError) {
            console.warn('AuthService: Failed to log mock profile update:', logError);
          }

          return mockUpdatedProfile;
        }
      }

      // Log profile update error
      try {
        await userActionLogger.logUserAction({
          type: 'update_profile_error',
          resourceType: 'user_profile',
          resourceId: currentProfile?.id,
          userId: currentProfile?.id,
          sessionId: userActionLogger['getSessionId']?.() || 'unknown',
          ipAddress: await userActionLogger['getClientIP']?.() || 'unknown',
          userAgent: navigator.userAgent,
          details: {
            error: error.message,
            statusCode: error.statusCode,
            updatedFields: Object.keys(profileData),
            timestamp: new Date().toISOString()
          }
        }, {
          component: 'AuthService',
          feature: 'profile_management'
        });
      } catch (logError) {
        console.warn('AuthService: Failed to log profile update error:', logError);
      }

      throw this.handleAuthError(error);
    }
  }

  /** Profile photo limits from server (MediaUpload.* profile photo keys). */
  async getProfilePhotoUploadLimits(): Promise<{
    maxSizeBytes: number;
    maxSizeMB: number;
    allowedImageFormats: string;
    maxImageWidth: number;
    maxImageHeight: number;
  }> {
    const r = await apiService.get<{
      maxSizeBytes?: number;
      maxSizeMB?: number;
      allowedImageFormats?: string;
      maxImageWidth?: number;
      maxImageHeight?: number;
    }>('/api/UserProfile/profile-photo-limits', { cache: false });
    const maxSizeBytes =
      typeof r.maxSizeBytes === 'number' && r.maxSizeBytes > 0
        ? r.maxSizeBytes
        : 5 * 1024 * 1024;
    const maxSizeMB =
      typeof r.maxSizeMB === 'number' && r.maxSizeMB > 0 ? r.maxSizeMB : maxSizeBytes / (1024 * 1024);
    const allowedImageFormats =
      typeof r.allowedImageFormats === 'string' && r.allowedImageFormats.trim() !== ''
        ? r.allowedImageFormats
        : 'jpg,jpeg,png,gif,webp';
    const maxImageWidth =
      typeof r.maxImageWidth === 'number' && r.maxImageWidth > 0 ? r.maxImageWidth : 4096;
    const maxImageHeight =
      typeof r.maxImageHeight === 'number' && r.maxImageHeight > 0 ? r.maxImageHeight : 4096;
    return { maxSizeBytes, maxSizeMB, allowedImageFormats, maxImageWidth, maxImageHeight };
  }

  async uploadProfilePhoto(file: File): Promise<UserProfileDto> {
    try {
      console.log('AuthService: Uploading profile photo:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      const formData = new FormData();
      formData.append('Photo', file); // Note: API expects 'Photo' not 'photo'

      console.log('AuthService: FormData created, making API request...');
      const serverProfile = await apiService.post<ServerUserProfileDto>('/api/UserProfile/profile-photo', formData);

      console.log('AuthService: Received response from API:', serverProfile);
      console.log('AuthService: Server profileImageUrl:', serverProfile.profileImageUrl);

      // Map server response to client interface
      const updatedProfile = this.mapServerProfileToClient(serverProfile);

      console.log('AuthService: Mapped to client profile:', updatedProfile);
      console.log('AuthService: Client profilePhoto URL:', updatedProfile.profilePhoto);

      // Clear cache to ensure fresh data on next fetch
      apiService.clearCache();

      // Update cached profile
      localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
      console.log('AuthService: Updated profile saved to localStorage');

      return updatedProfile;
    } catch (error) {
      // If API is not available, create a mock response for development
      if (import.meta.env.DEV && error.statusCode === 404) {
        console.warn('Photo upload API not available, using mock response');

        const currentProfile = this.getCurrentUser();
        if (currentProfile) {
          // Convert image to base64 data URL for persistence
          const mockPhotoUrl = await this.convertFileToDataURL(file);
          console.log('Generated photo URL length:', mockPhotoUrl.length);
          console.log('Photo URL preview:', mockPhotoUrl.substring(0, 50) + '...');

          const mockUpdatedProfile = {
            ...currentProfile,
            profilePhoto: mockPhotoUrl,
            // Update last login to show activity
            lastLoginAt: new Date().toISOString(),
          };

          // Update cached profile
          localStorage.setItem('userProfile', JSON.stringify(mockUpdatedProfile));
          console.log('Saved profile to localStorage with photo');
          console.log('Updated profile:', mockUpdatedProfile);

          return mockUpdatedProfile;
        }
      }

      throw this.handleAuthError(error);
    }
  }

  async changePassword(passwordData: ChangePasswordDto): Promise<AuthResponseDto> {
    try {
      const response = await apiService.post<AuthResponseDto>('/api/Auth/change-password', passwordData);

      // If a new token is provided, update it
      if (response.success && response.token) {
        this.setTokenWithExpiration(response.token);
      }

      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  async forgotPassword(email: string): Promise<AuthResponseDto> {
    try {
      const response = await apiService.post<AuthResponseDto>('/api/Auth/forgot-password', { email });
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<AuthResponseDto> {
    try {
      const response = await apiService.post<AuthResponseDto>('/api/Auth/reset-password', {
        token,
        newPassword
      });
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  async refreshToken(): Promise<string> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    this.refreshTokenPromise = this.performTokenRefresh();

    try {
      const newToken = await this.refreshTokenPromise;
      return newToken;
    } finally {
      this.refreshTokenPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<string> {
    try {
      const currentToken = localStorage.getItem('authToken');
      const response = await apiService.post<{ token: string }>('/api/Auth/refresh', {
        token: currentToken
      });

      if (response.token) {
        this.setTokenWithExpiration(response.token);
        return response.token;
      }

      throw new Error('No token received from refresh endpoint');
    } catch (error: unknown) {
      throw this.handleAuthError(error);
    }
  }

  async logout(logoutReason: string = 'user_initiated') {
    const intentional = logoutReason === 'user_initiated';
    if (intentional) {
      (window as any).__intentionalLogoutInProgress = true;
      try {
        sessionStorage.setItem('skipSessionExpiredToast', '1');
      } catch {
        /* ignore */
      }
    }

    // Try to get userId before clearing auth data
    let userId = this.getUserId();
    
    // If no userId from token (session expired), try to get from user profile
    if (!userId) {
      try {
        const userProfile = localStorage.getItem('userProfile');
        if (userProfile) {
          const profile = JSON.parse(userProfile);
          userId = profile.id || profile.email;
        }
      } catch (e) {
        console.warn('AuthService: Failed to get userId from profile:', e);
      }
    }
    
    const sessionDuration = this.getSessionDuration();

    try {
      const { disableWebPushOnThisDevice } = await import('./pushService');
      await disableWebPushOnThisDevice();
    } catch {
      /* still log out if this device cannot unsubscribe */
    }

    // Call backend logout endpoint to log the action server-side before clearing token
    // This ensures the logout is logged even if token is cleared
    try {
      if (userId && userId !== 'expired_session') {
        // Try to call backend logout endpoint if token is still valid
        try {
          await apiService.post('/api/Auth/logout', {
            logoutReason,
            sessionDuration
          }).catch(() => {
            // If backend call fails (token already invalid), log via frontend logger
            userActionLogger.logLogout(userId, logoutReason, sessionDuration).catch(() => {});
          });
        } catch (error) {
          // Token might be invalid, log via frontend logger as fallback
          userActionLogger.logLogout(userId, logoutReason, sessionDuration).catch(() => {});
        }
      } else {
        // Log anonymous logout for expired sessions
        userActionLogger.logLogout(
          'expired_session',
          logoutReason === 'user_initiated' ? 'expired_session_manual' : logoutReason,
          sessionDuration
        ).catch(() => {});
      }
    } catch (error) {
      // Fallback: try frontend logger if backend call fails
      if (userId) {
        userActionLogger.logLogout(userId, logoutReason, sessionDuration).catch(() => {});
      }
    }

    this.clearAuthData();

    // Dispatch logout event for other components to listen
    window.dispatchEvent(new CustomEvent('auth:logout', {
      detail: { reason: logoutReason, intentional }
    }));

    if (intentional) {
      // Keep the flag briefly so in-flight 401 handlers still skip session-expired redirects
      window.setTimeout(() => {
        (window as any).__intentionalLogoutInProgress = false;
      }, 2000);
    }
  }

  private clearAuthData() {
    apiService.clearToken();
    apiService.clearCache(); // Clear API cache to prevent stale data from previous user
    localStorage.removeItem('authToken');
    localStorage.removeItem('authTokenInfo');
    localStorage.removeItem('userProfile');
    clearLearnerPersistedFilters();

    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
      this.tokenExpirationTimer = null;
    }

  }

  isAuthenticated(): boolean {
    // Persist session until explicit Sign out — expired JWTs are refreshed on demand.
    return !!localStorage.getItem('authToken');
  }

  hasRole(role: string): boolean {
    const tokenInfo = this.getTokenInfo();
    return tokenInfo?.roles.includes(role) || false;
  }

  hasAdminRole(): boolean {
    return this.hasRole('Admin') || this.hasRole('Administrator');
  }

  getCurrentUser(): UserProfileDto | null {
    const userProfile = localStorage.getItem('userProfile');
    if (!userProfile) return null;

    try {
      return JSON.parse(userProfile);
    } catch {
      return null;
    }
  }

  getTokenInfo(): TokenInfo | null {
    const tokenInfo = localStorage.getItem('authTokenInfo');
    if (!tokenInfo) return null;

    try {
      return JSON.parse(tokenInfo);
    } catch {
      return null;
    }
  }

  getUserId(): string | null {
    return this.getTokenInfo()?.userId || null;
  }

  getUserRoles(): string[] {
    return this.getTokenInfo()?.roles || [];
  }

  isTokenExpired(): boolean {
    const tokenInfo = this.getTokenInfo();
    if (!tokenInfo) return true;

    const expirationTime = new Date(tokenInfo.expiresAt).getTime();
    return Date.now() >= expirationTime;
  }

  getTokenExpirationTime(): Date | null {
    const tokenInfo = this.getTokenInfo();
    return tokenInfo ? new Date(tokenInfo.expiresAt) : null;
  }

  private getSessionDuration(): number | undefined {
    const tokenInfo = this.getTokenInfo();
    if (!tokenInfo) return undefined;

    const issuedAt = new Date(tokenInfo.issuedAt).getTime();
    const currentTime = Date.now();
    return currentTime - issuedAt;
  }

  private handleAuthError(error: any): AuthError {
    const authError = new Error(error.message || 'Authentication failed') as AuthError;
    authError.statusCode = error.statusCode || 500;
    authError.details = error.details;

    // Add validation errors if present
    if (error.errors) {
      (authError as any).errors = error.errors;
    }

    // Learner sessions are cleared only by logout() — see sessionExpirationPolicy.
    return authError;
  }

  // Method to check if user needs to re-authenticate
  requiresReAuthentication(): boolean {
    return !this.isAuthenticated();
  }

  // Method to get authentication headers for manual requests
  getAuthHeaders(): Record<string, string> {
    const tokenInfo = this.getTokenInfo();
    return tokenInfo ? { Authorization: `Bearer ${tokenInfo.token}` } : {};
  }

  // Method to validate current session
  async validateSession(): Promise<boolean> {
    if (!this.isAuthenticated()) return false;

    try {
      if (this.isTokenExpired() || this.isTokenExpiringSoon()) {
        await this.refreshToken();
      }
      await this.getProfile();
      return true;
    } catch (error: unknown) {
      const status = (error as { statusCode?: number; status?: number })?.statusCode
        ?? (error as { status?: number })?.status;
      // Learner rule: keep the stored session when profile validation is rejected.
      if (status === 401) {
        return false;
      }
      return false;
    }
  }

  /**
   * Convert a File to a base64 data URL for persistent storage
   */
  private convertFileToDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        if (event.target?.result) {
          resolve(event.target.result as string);
        } else {
          reject(new Error('Failed to read file'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };

      reader.readAsDataURL(file);
    });
  }
}

export const authService = new AuthService();